import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, TrendingUp, TrendingDown, FileText, ArrowRight } from 'lucide-react';
import { MonthlyPerformanceData } from './types';
import { formatPercentage } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface MonthlyReviewSectionProps {
  monthlyData: MonthlyPerformanceData | null;
  loading: boolean;
}

export const MonthlyReviewSection: React.FC<MonthlyReviewSectionProps> = ({
  monthlyData,
  loading,
}) => {
  const { toast } = useToast();

  const handleGenerateReport = () => {
    toast({
      title: "Report Generation Started",
      description: "Your monthly performance report is being generated and will be available shortly.",
      variant: "info"
    });
    // In a real app, trigger an API call here
  };

  const renderLoadingState = () => (
     <Card>
      <CardHeader>
        <Skeleton className="h-6 w-3/5 mb-1" />
        <Skeleton className="h-4 w-1/2" />
      </CardHeader>
      <CardContent className="space-y-4">
         <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
         </div>
         <Skeleton className="h-10 w-full" />
         <Skeleton className="h-20 w-full" />
         <div className="flex justify-end">
             <Skeleton className="h-10 w-1/3" />
         </div>
      </CardContent>
    </Card>
  );

 const renderEmptyState = () => (
     <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Monthly Review</CardTitle>
        <CardDescription>Performance summary for the last completed month.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center h-48 text-center border border-dashed rounded-lg bg-gray-50">
            <Calendar className="h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-500">Monthly Data Pending</h3>
            <p className="text-sm text-gray-400 max-w-xs mt-1">
              Check back after the end of the month for performance analysis.
            </p>
        </div>
      </CardContent>
     </Card>
 );


  if (loading) {
    return renderLoadingState();
  }

  if (!monthlyData) {
    return renderEmptyState();
  }

  const {
    month,
    returnPercent,
    benchmarkReturnPercent,
    topContributor,
    bottomContributor,
    keyEvents,
    aiCommentary
  } = monthlyData;

  const isPositiveReturn = returnPercent >= 0;
  const outperformance = benchmarkReturnPercent !== undefined ? returnPercent - benchmarkReturnPercent : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Monthly Review: {month}</CardTitle>
        <CardDescription>Performance summary for the last completed month.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">Monthly Return</div>
            <div className={`text-xl font-semibold flex items-center ${isPositiveReturn ? 'text-green-600' : 'text-red-600'}`}>
              {isPositiveReturn ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
              {formatPercentage(returnPercent)}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">vs Benchmark</div>
            <div className={`text-xl font-semibold ${outperformance === null ? 'text-gray-600' : outperformance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {outperformance !== null ? `${outperformance >= 0 ? '+' : ''}${formatPercentage(outperformance)}` : 'N/A'}
            </div>
          </div>
           <div className="p-3 bg-gray-50 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">Contributors</div>
            <div className="text-sm font-medium text-green-600 truncate" title={`Top: ${topContributor}`}>Top: {topContributor || 'N/A'}</div>
             <div className="text-sm font-medium text-red-600 truncate" title={`Bottom: ${bottomContributor}`}>Bottom: {bottomContributor || 'N/A'}</div>
          </div>
        </div>

        {aiCommentary && (
           <div className="p-3 border rounded-lg bg-blue-50 border-blue-100">
             <h4 className="text-sm font-medium text-blue-700 mb-1">Stewie's Monthly Commentary</h4>
             <p className="text-sm text-blue-900">{aiCommentary}</p>
           </div>
        )}

        {keyEvents && keyEvents.length > 0 && (
            <div>
                <h4 className="text-sm font-medium text-gray-600 mb-2">Key Events This Month</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 pl-1">
                    {keyEvents.map((event, index) => (
                        <li key={index}>{event}</li>
                    ))}
                </ul>
            </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button onClick={handleGenerateReport} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Generate Monthly Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
